import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { Button, StatusPill } from '@dia/ui';
import { useAuth } from '../auth';
import { parsePairingInput } from './lib/pairing';
import { Field, HeaderActions, inputClass, Notice } from './lib/ui';
import { Screen } from '@dia/ui';

/**
 * The one time pairing link or QR, opened on the phone that will be used.
 *
 * The code arrives in the URL, is exchanged once for a device token, and is
 * then dead. Nothing about this screen is reusable, which is the point. If the
 * link was copied badly, the code can also be pasted in by hand.
 */
export function Pair() {
  const { t, lang } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pair, loading, error, paired } = useAuth();
  const [done, setDone] = useState(false);
  const [pasted, setPasted] = useState('');
  const [badPaste, setBadPaste] = useState(false);

  const code = params.get('code') ?? '';
  // A code is spent by the first try. Remembering which one was tried keeps a
  // re-render (or StrictMode's second run) from spending it twice and then
  // reporting "that link no longer works" for a link that just worked.
  const tried = useRef<string | null>(null);

  useEffect(() => {
    // A phone that is already paired still takes a new link: pairing it again
    // for somebody else, or after the owner revoked it, is a real thing to do.
    // The old test skipped the exchange and said "already paired".
    if (!code || tried.current === code) return;
    tried.current = code;
    void pair(code, navigator.userAgent.slice(0, 60)).then((ok) => setDone(ok));
  }, [code, pair]);

  const onPaste = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = parsePairingInput(pasted);
    setBadPaste(parsed === null);
    if (parsed) setDone(await pair(parsed, navigator.userAgent.slice(0, 60)));
  };

  const ready = done || paired;

  return (
    <Screen title={t('ops.pair.title')} actions={<HeaderActions badge={false} />}>
      <div className="mt-4 flex flex-col gap-6">
        {loading && <StatusPill tone="info" className="text-body">{t('loading')}</StatusPill>}

        {!code && !ready && <p>{t('ops.pair.no_code')}</p>}

        {error && (
          <p role="alert" className="text-danger">
            {lang === 'es' ? error.es : error.en}
          </p>
        )}

        {ready ? (
          <>
            {(done || !error) && <Notice tone="ok">{done ? t('ops.pair.done') : t('ops.pair.already')}</Notice>}
            <Button block size="lg" onClick={() => navigate('/entrar')}>
              {t('ops.login.submit')}
            </Button>
          </>
        ) : (
          <form onSubmit={onPaste} className="flex flex-col gap-3">
            <Field label={t('ops.pair.paste_label')}>
              <textarea
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={3}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className={inputClass}
              />
            </Field>
            {badPaste && <Notice tone="danger">{t('ops.pair.bad_code')}</Notice>}
            <Button type="submit" block size="lg" busy={loading} busyLabel={t('loading')}>
              {t('ops.pair.paste_submit')}
            </Button>
          </form>
        )}
      </div>
    </Screen>
  );
}
